import React, { useEffect, useState } from 'react';
import { Button, Card } from '@/components/ui';
import { ProjectDocument, ProjectDocumentCreateData } from '../../../types/ProjectDocument';
import { projectService } from '../../../services/projectService';
import OtherDocumentList from './OtherDocumentList';
import OtherDocumentModal from './OtherDocumentModal';

interface OtherDocumentsTabProps {
  projectId: number;
}

const OtherDocumentsTab: React.FC<OtherDocumentsTabProps> = ({ projectId }) => {
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [refreshingId, setRefreshingId] = useState<number | null>(null);

  useEffect(() => {
    if (!projectId) return;
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await (projectService as any).getProjectOtherDocuments(projectId);
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading other documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocument = () => {
    setIsModalVisible(true);
  };

  const handleSaveDocument = async (docData: ProjectDocumentCreateData, file?: File) => {
    try {
      if (!file) throw new Error('PDF file is required');
      await (projectService as any).createProjectOtherDocument(projectId, docData, file);
      setIsModalVisible(false);
      await loadDocuments();
    } catch (error) {
      console.error('Error saving document:', error);
      throw error;
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    try {
      setRefreshingId(docId);
      await (projectService as any).deleteProjectOtherDocument(projectId, docId);
      await loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    } finally {
      setRefreshingId(null);
    }
  };

  const handleDownloadDocument = (doc: ProjectDocument) => {
    const attachmentURL = doc.attachmentURL;
    if (attachmentURL?.startsWith?.('placeholder://')) {
      alert('File upload failed. This document could not be uploaded to storage. Please try uploading the file again.');
      return;
    }
    window.open(`/api/projects/${projectId}/other-documents/${doc.id}/download`, '_blank');
  };

  return (
    <Card>
      <Card.Header className="tw-flex tw-justify-between tw-items-center">
        <h6 className="tw-mb-0">Other documents</h6>
        <Button variant="primary" size="sm" onClick={handleAddDocument}>
          <i className="bi bi-plus-circle tw-mr-2"></i>
          Add Document
        </Button>
      </Card.Header>
      <Card.Body>
        <OtherDocumentList
          documents={documents}
          loading={loading}
          onDownload={handleDownloadDocument}
          onDelete={handleDeleteDocument}
          refreshingId={refreshingId}
        />
      </Card.Body>

      <OtherDocumentModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSave={handleSaveDocument}
      />
    </Card>
  );
};

export default OtherDocumentsTab;

